import React, { FunctionComponent, useEffect, useState } from 'react';
import domPurify from 'dompurify';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { joinStrings } from '../../utils/string';
import { Heading } from '../shared/Heading';
import { Button } from '../shared/Button';
import { PopUpHandler } from '../shared/PopupHandler';
import { Paragraph } from '../shared/Paragraph';
import { getPromoCodeParam } from '../../hooks/registration/useRegistrationStepper.helper';
import { locale } from '../../consts';
import { getImageUrl } from '../../utils/navigation';
import { Endpoint } from '../../models/api.model';
import { ApolloError } from '@apollo/client';
import {
  MagicxPromoGetBySlugQuery,
  useMagicxPromoGetBySlugLazyQuery,
} from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/promo-get-by-slug.query.generated';
import './styles';

type WelcomeBonusBannerDetails = {
  termsContent?: string;
  title?: string;
  description?: string;
  imageMobile?: string;
  imageDesktop?: string;
};

export const WelcomeBonusBanner: FunctionComponent = () => {
  const { isMobileDevice } = useDeviceInfo();
  const sanitizer = domPurify.sanitize;

  const [isPopupVisible, setIsPopupVisible] = useState<boolean>(false);
  const [bonus, setBonus] = useState<WelcomeBonusBannerDetails | null>(null);
  const { promoValue } = getPromoCodeParam();

  const processPromo = (bonus: MagicxPromoGetBySlugQuery): void => {
    const translations = bonus?.magicx_promotions?.[0].translations?.[0];
    const thumbNail = bonus?.magicx_promotions?.[0]?.thumbnail;

    setBonus({
      termsContent: translations?.terms,
      title: translations?.title,
      description: translations?.description,
      imageMobile: getImageUrl(thumbNail?.filename_disk),
      imageDesktop: getImageUrl(thumbNail?.filename_disk),
    } as WelcomeBonusBannerDetails);
  };

  const [fetchBonusDetailsBySlug] = useMagicxPromoGetBySlugLazyQuery({
    variables: {
      language: locale,
      slugs: [promoValue || 'defaultwelcomepromo'],
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: processPromo,
    onError: (e: ApolloError) => {
      console.error(e.message);
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  useEffect(() => {
    fetchBonusDetailsBySlug();
  }, []);

  return (
    <>
      <div
        style={{ backgroundImage: isMobileDevice ? `url(${bonus?.imageMobile})` : `url(${bonus?.imageDesktop})` }}
        className={joinStrings([
          'welcome-bonus-banner',
          isMobileDevice && 'welcome-bonus-banner--mobile',
          isMobileDevice &&
            ((bonus?.description?.length || 0) > 700
              ? 'welcome-bonus-banner--mobile-expanded'
              : 'welcome-bonus-banner--mobile-compact'),
        ])}
      >
        <div
          className={joinStrings([
            'welcome-bonus-banner__title-wrapper',
            isMobileDevice && 'welcome-bonus-banner__title-wrapper--mobile',
          ])}
        >
          <Heading level={3} styleAs={3} className="welcome-bonus-banner__title">
            {bonus?.title || ''}
          </Heading>
          <Paragraph className="welcome-bonus-banner__description">{bonus?.description}</Paragraph>
        </div>
        {bonus?.termsContent && (
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={(): void => {
              setIsPopupVisible(true);
            }}
          >
            {isMobileDevice ? 'Terms' : 'Full terms'}
          </Button>
        )}
      </div>
      {isPopupVisible && (
        <PopUpHandler
          isOpen
          onClose={(): void => {
            setIsPopupVisible(false);
          }}
          header="Welcome Bonus Details"
          modalProps={{
            className: 'welcome-bonus-banner__modal',
            'aria-label': 'privacy policy heading',
          }}
        >
          <Paragraph dangerouslySetInnerHTML={{ __html: sanitizer(bonus?.termsContent || '') }} />
          <Button
            type="button"
            size="x-large"
            className="terms-back-btn"
            onClick={(): void => {
              setIsPopupVisible(false);
            }}
          >
            Continue Registration
          </Button>
        </PopUpHandler>
      )}
    </>
  );
};
